import { Currency, Language } from '@/lib/interfaces/interfaces'
import { CartItem } from '@/lib/interfaces/ordering'
import { Minus, Plus, Trash2, X } from 'lucide-react'
import { formatMoney } from '@/lib/utils/format'

// Objeto de traducciones para cada idioma
const translations = {
  es: {
    orderTitle: 'Tu Pedido',
    closeCart: 'Cerrar carrito',
    removeProduct: 'Eliminar producto',
    decrement: 'Decrementar cantidad',
    increment: 'Incrementar cantidad',
    price: 'Precio'
  },
  en: {
    orderTitle: 'Your Order',
    closeCart: 'Close cart',
    removeProduct: 'Remove product',
    decrement: 'Decrease quantity',
    increment: 'Increase quantity',
    price: 'Price'
  },
  fr: {
    orderTitle: 'Votre commande',
    closeCart: 'Fermer le panier',
    removeProduct: 'Supprimer le produit',
    decrement: 'Diminuer la quantité',
    increment: 'Augmenter la quantité',
    price: 'Prix'
  },
  ca: {
    orderTitle: 'La teva Comanda',
    closeCart: 'Tancar cistella',
    removeProduct: 'Eliminar producte',
    decrement: 'Decrementar quantitat',
    increment: 'Incrementar quantitat',
    price: 'Preu'
  },
  de: {
    orderTitle: 'Ihre Bestellung',
    closeCart: 'Warenkorb schließen',
    removeProduct: 'Produkt entfernen',
    decrement: 'Menge verringern',
    increment: 'Menge erhöhen',
    price: 'Preis'
  },
  eu: {
    orderTitle: 'Zure eskaera',
    closeCart: 'Itxi saskia',
    removeProduct: 'Kendu produktua',
    decrement: 'Gutxitu kopurua',
    increment: 'Handitu kopurua',
    price: 'Prezioa'
  }
}

interface Props {
  toggleCart: () => void
  cart: CartItem[]
  removeFromCart: (itemId: string, variantType: string) => void
  handleQuantityChange: (itemId: string, variantType: string, quantity: number) => void
  language: Language
  currency: Currency
}

const CartList = ({
  cart,
  handleQuantityChange,
  removeFromCart,
  toggleCart,
  language,
  currency
}: Props) => {
  const texts = translations[language] || translations.es

  return (
    <>
      <div className='orderingCart__container__header'>
        <h2 className='orderingCart__container__header__title'>
          {texts.orderTitle}
        </h2>
        <button
          onClick={toggleCart}
          aria-label={texts.closeCart}
          className='orderingCart__container__header__close'
        >
          <X size={20} />
        </button>
      </div>
      <div className='orderingCart__container__divider' />

      {/* Lista de productos en el carrito */}
      <div className='orderingCart__container__productList'>
        <ul className='orderingCart__product-list'>
          {cart.map((item) => {
            // Indicador: nombre personalizado si existe; si no, "1/2" para
            // media y "1/3" para pequeña. La entera (full) no muestra tag.
            const variantIndicator =
              item.variant.label ||
              (item.variant.type === 'half'
                ? '1/2'
                : item.variant.type === 'tapa'
                  ? '1/3'
                  : '')
            return (
              <li
                key={`${item.id}-${item.variant.type}`}
                className='orderingCart__product-item'
              >
                <button
                  onClick={() =>
                    removeFromCart(item.id, item.variant.type)}
                  className='orderingCart__product-item__remove'
                  aria-label={texts.removeProduct}
                >
                  <Trash2 size={16} />
                </button>
                <div className='orderingCart__product-item__info'>
                  <div className='orderingCart__product-item__name'>
                    {item.name}
                    {variantIndicator && <span className='orderingCart__variant-tag'>{variantIndicator}</span>}
                  </div>
                  <div className='orderingCart__product-item__price'>
                    {texts.price}: {formatMoney(item.variant.price, currency)}
                  </div>
                </div>
                <div className='orderingCart__product-item__actions'>
                  <div className='orderingCart__quantity-control'>
                    <button
                      onClick={() =>
                        handleQuantityChange(
                          item.id,
                          item.variant.type,
                          item.quantity - 1
                        )}
                      aria-label={texts.decrement}
                      className='orderingCart__quantity-btn'
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type='number'
                      value={item.quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value)
                        handleQuantityChange(
                          item.id,
                          item.variant.type,
                          isNaN(val) || val < 1 ? 1 : val
                        )
                      }}
                      min={1}
                      className='orderingCart__quantity-input'
                    />
                    <button
                      onClick={() =>
                        handleQuantityChange(
                          item.id,
                          item.variant.type,
                          item.quantity + 1
                        )}
                      aria-label={texts.increment}
                      className='orderingCart__quantity-btn'
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </>
  )
}

export default CartList
